import { Target } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FreelancerListItem } from "@/types/freelancer";
import { SCORE_WEIGHTS, SCORE_FORMULA_DESC } from "@/utils/score-reputacao";
import { formatTimeAgo } from "@/utils/dateUtils";

type ScoreFreelancer = Pick<
  FreelancerListItem,
  | "score_reputacao"
  | "total_sinais"
  | "total_selecoes"
  | "total_shortlists"
  | "total_aprovacoes"
  | "total_projetos_concluidos"
  | "total_drops"
  | "taxa_conversao"
  | "total_vagas_selecionado"
  | "total_vagas_shortlist_inclusivo"
  | "taxa_conversao_shortlist"
  | "ultimo_sinal_at"
  | "total_views"
>;

interface FreelancerScoreBadgeProps {
  freelancer: ScoreFreelancer;
  size?: "sm" | "md";
  className?: string;
}

interface CountRow {
  label: string;
  value: string | number;
  show: boolean;
}

function ScoreBreakdownContent({ freelancer }: { freelancer: ScoreFreelancer }) {
  const score = freelancer.score_reputacao;
  const hasScore = typeof score === "number";
  const rounded = hasScore ? Math.round(score!) : null;

  const counts: CountRow[] = [
    {
      label: "Sinais totais",
      value: freelancer.total_sinais ?? 0,
      show: (freelancer.total_sinais ?? 0) > 0,
    },
    {
      label: "Aprovações",
      value: freelancer.total_aprovacoes ?? 0,
      show: (freelancer.total_aprovacoes ?? 0) > 0,
    },
    {
      label: "Projetos concluídos",
      value: freelancer.total_projetos_concluidos ?? 0,
      show: (freelancer.total_projetos_concluidos ?? 0) > 0,
    },
    {
      label: "Shortlists",
      value: freelancer.total_shortlists ?? 0,
      show: (freelancer.total_shortlists ?? 0) > 0,
    },
    {
      label: "Seleções",
      value: freelancer.total_selecoes ?? 0,
      show: (freelancer.total_selecoes ?? 0) > 0,
    },
    {
      label: "Drops",
      value: freelancer.total_drops ?? 0,
      show: (freelancer.total_drops ?? 0) > 0,
    },
    {
      label: "Taxa de conversão (passou pela shortlist)",
      value:
        typeof freelancer.taxa_conversao_shortlist === "number"
          ? `${Math.round(freelancer.taxa_conversao_shortlist)}%`
          : "—",
      show:
        typeof freelancer.taxa_conversao_shortlist === "number" &&
        (freelancer.total_vagas_selecionado ?? 0) > 0,
    },
  ];

  const visibleCounts = counts.filter((c) => c.show);
  const ultimoSinal = freelancer.ultimo_sinal_at
    ? formatTimeAgo(freelancer.ultimo_sinal_at).text
    : null;
  const views = freelancer.total_views ?? 0;

  return (
    <div className="space-y-3 text-xs max-w-md">
      <div>
        <p className="font-semibold text-sm">
          Score de reputação: {hasScore ? rounded : "—"} / 100
        </p>
        {!hasScore && (
          <p className="italic text-muted-foreground mt-1">
            Sem sinais registrados ainda.
          </p>
        )}
      </div>

      {(visibleCounts.length > 0 || ultimoSinal || views > 0) && (
        <div>
          <p className="font-medium mb-1">Suas contagens</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {visibleCounts.map((c) => (
              <div key={c.label} className="flex justify-between gap-2">
                <span className="text-muted-foreground">{c.label}:</span>
                <span className="font-medium">{c.value}</span>
              </div>
            ))}
            {ultimoSinal && (
              <div className="flex justify-between gap-2 col-span-2">
                <span className="text-muted-foreground">Último sinal:</span>
                <span className="font-medium">{ultimoSinal}</span>
              </div>
            )}
          </div>
          {views > 0 && (
            <p className="text-muted-foreground mt-1 italic">
              Visualizações: {views} (não entram no score)
            </p>
          )}
        </div>
      )}

      <div>
        <p className="font-medium mb-1">Pesos por sinal</p>
        <table className="w-full border-separate border-spacing-x-2 border-spacing-y-0">
          <tbody>
            {SCORE_WEIGHTS.map((w) => (
              <tr key={w.tipo}>
                <td className="text-muted-foreground">{w.label}</td>
                <td
                  className={cn(
                    "text-right font-mono tabular-nums",
                    w.peso > 0 ? "text-emerald-700" : "text-rose-700",
                  )}
                >
                  {w.peso > 0 ? "+" : ""}
                  {w.peso.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-muted-foreground italic border-t pt-2">
        {SCORE_FORMULA_DESC}
      </p>
    </div>
  );
}

/**
 * Badge do score composto de reputação do freelancer. Cor varia conforme a faixa.
 * Mostra "—" quando o freelancer ainda não tem sinais.
 */
export function FreelancerScoreBadge({
  freelancer,
  size = "md",
  className,
}: FreelancerScoreBadgeProps) {
  const score = freelancer.score_reputacao;
  const hasScore = typeof score === "number";
  const rounded = hasScore ? Math.round(score!) : null;

  const colorClass = !hasScore
    ? "bg-slate-100 text-slate-500 border-slate-200"
    : rounded! >= 75
      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
      : rounded! >= 50
        ? "bg-blue-100 text-blue-800 border-blue-300"
        : rounded! >= 25
          ? "bg-amber-50 text-amber-800 border-amber-300"
          : "bg-rose-50 text-rose-800 border-rose-300";

  const sizeClass =
    size === "sm" ? "text-xs px-1.5 py-0.5 gap-1" : "text-sm px-2 py-0.5 gap-1.5";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center rounded-full border font-semibold",
              sizeClass,
              colorClass,
              className,
            )}
          >
            <Target className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
            <span>{hasScore ? rounded : "—"}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-md">
          <ScoreBreakdownContent freelancer={freelancer} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
